import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Linkedin, Mail, MoveRight, Quote } from "lucide-react";
import chennaImage from "@/assets/team/chenna.jpeg";
const TeamPage = () => {
  const team = [
    {
      name: "Chenna Hoskera",
      role: "Managing Director",
      image: chennaImage,
      description:
        "Leads BRYN with strategic clarity, client-first thinking, and deep execution experience across complex interior projects.",
      focus: "Business strategy, client leadership, and end-to-end delivery.",
      accent: "from-primary via-primary/90 to-primary/70",
    },
    {
      name: "Suchitha Patil",
      role: "Procurement Lead",
      imageWrapClass:
        "flex items-center justify-center bg-[linear-gradient(180deg,#f5f3ef_0%,#ebe6de_100%)]",
      description:
        "Keeps sourcing and vendor coordination running smoothly, helping every project stay aligned with quality expectations, timelines, and budgets.",
      focus: "Procurement planning, vendor coordination, and material readiness.",
      accent: "from-charcoal via-charcoal to-charcoal/80",
    },
  ];

  const highlights = [
    "Two leaders involved from concept through handover",
    "Design and execution aligned from day one",
    "Mobile-first layout that keeps content readable and balanced",
  ];

  return (
    <div className="overflow-hidden bg-background">
      <section className="relative isolate min-h-[60vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=1920&auto=format&fit=crop"
            alt="BRYN leadership team"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/85 to-charcoal/65" />
        </div>
        <div className="absolute -left-16 top-28 h-48 w-48 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute bottom-10 right-0 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

        <div className="relative z-10 container mx-auto px-4 pb-16 pt-28 lg:px-8 lg:pb-24">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_360px] lg:items-end">
            <div className="max-w-3xl">
              <Badge className="mb-5 border-0 bg-primary/20 text-primary-foreground">
                Our Team
              </Badge>
              <h1 className="font-display text-4xl font-bold leading-tight text-primary-foreground sm:text-5xl lg:text-6xl">
                A focused two-person leadership team with hands-on design ownership.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
                BRYN keeps leadership close to the work. You collaborate directly
                with the people shaping the creative vision and guiding execution,
                giving every project a more personal and accountable process.
              </p>
            </div>

            <div className="grid gap-4 rounded-[28px] border border-white/10 bg-white/10 p-5 backdrop-blur-md sm:grid-cols-3 lg:grid-cols-1">
              {highlights.map((item) => (
                <div
                  key={item}
                  className="rounded-2xl border border-white/10 bg-black/10 p-4"
                >
                  <p className="text-sm leading-relaxed text-primary-foreground/80">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
                Leadership
              </Badge>
              <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl">
                Two specialists. One seamless studio experience.
              </h2>
            </div>
          </div>

          <div className="grid gap-8 xl:grid-cols-2">
            {team.map((member, index) => (
              <article
                key={member.name}
                className="group overflow-hidden rounded-[32px] border border-border/60 bg-card shadow-md transition-all duration-500 hover:-translate-y-1 hover:shadow-elegant"
              >
                <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
                  <div
                    className={`relative min-h-[320px] overflow-hidden sm:min-h-[380px] ${
                      member.imageWrapClass ?? ""
                    }`}
                  >
                    {member.image ? (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : null}
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/30 to-transparent" />
                    <div
                      className={`absolute left-4 top-4 h-24 w-24 rounded-full bg-gradient-to-br ${member.accent} opacity-70 blur-2xl`}
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                      <p className="text-sm uppercase tracking-[0.28em] text-primary-foreground/60">
                        Team 0{index + 1}
                      </p>
                      <h3 className="mt-2 font-display text-2xl font-semibold text-primary-foreground sm:text-3xl">
                        {member.name}
                      </h3>
                      <p className="mt-1 text-sm font-medium text-primary sm:text-base">
                        {member.role}
                      </p>
                    </div>
                  </div>

                  <div className="flex h-full flex-col justify-between p-6 sm:p-8">
                    <div>
                      <div className="mb-6 inline-flex rounded-full bg-muted px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                        Core Focus
                      </div>
                      <p className="text-base leading-relaxed text-foreground/90">
                        {member.description}
                      </p>
                      <p className="mt-5 rounded-2xl bg-muted/70 p-4 text-sm leading-relaxed text-muted-foreground">
                        {member.focus}
                      </p>
                    </div>

                    <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                      <div className="flex gap-3">
                        <a
                          href="#"
                          aria-label={`${member.name} on LinkedIn`}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-background transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                        >
                          <Linkedin className="h-4 w-4" />
                        </a>
                        <a
                          href="mailto:care@bryninteriors.com"
                          aria-label={`Email ${member.name}`}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-background transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                        >
                          <Mail className="h-4 w-4" />
                        </a>
                      </div>
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                        Direct involvement
                        <MoveRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid gap-8 rounded-[32px] bg-muted p-6 sm:p-8 lg:grid-cols-[1.1fr_0.9fr] lg:p-12">
            <div>
              <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
                Studio Promise
              </Badge>
              <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
                You work with decision-makers, not layers of handoff.
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
                Every BRYN project benefits from a tightly connected leadership
                rhythm: one perspective anchors business outcomes and delivery,
                while the other protects design quality and experience. That
                balance helps projects move faster and feel more intentional.
              </p>
            </div>

            <div className="rounded-[28px] bg-charcoal p-6 text-primary-foreground sm:p-8">
              <Quote className="h-10 w-10 text-primary" />
              <p className="mt-6 text-lg leading-relaxed text-primary-foreground/85">
                The person you meet on day one stays actively involved until the
                final handover, ensuring continuity, trust, and better outcomes.
              </p>
              <div className="mt-8 h-px w-full bg-white/10" />
              <p className="mt-6 text-sm uppercase tracking-[0.24em] text-primary-foreground/60">
                BRYN Interiors
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-charcoal py-16 text-primary-foreground sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 text-center lg:px-8">
          <Badge variant="outline" className="mb-4 border-primary/40 text-primary">
            Start Your Project
          </Badge>
          <h2 className="font-display text-3xl font-bold sm:text-4xl">
            Ready to build with BRYN&apos;s leadership team?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/75">
            Share your vision with us and collaborate directly with the two
            teammates leading design thinking and delivery from the first
            conversation.
          </p>
          <Button asChild size="lg" className="mt-8 bg-primary px-8 hover:bg-primary/90">
            <Link to="/contact">
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
